import {
    DocumentSlot,
    SitePerspectivePhase,
    SitePerspectiveProjectType,
    documentSlotsFor,
    processFor,
} from '@the-real-insight/tri-model';

import { ProcessTypeKey } from './processModel';

/**
 * Was in dieser Phase an Dokumenten fehlt.
 *
 * Der Datenraum zeigt, was da ist. Die interessantere Frage ist, was **nicht** da ist: Eine
 * Vollständigkeitsprüfung, die nur das Vorhandene zeigt, beantwortet sie nicht — man müsste die
 * Anforderungsliste daneben legen und abhaken. Die Platzhalter tun das an der Stelle, an der man
 * ohnehin hinsieht.
 *
 * Verglichen wird über Rolle und Untertyp, nicht über Dateinamen. Wie eine Datei heißt, ist
 * Zufall; was sie *ist*, hat die Auswertung festgestellt, und genau darauf zeigt die Anforderung.
 */

/** Der Prozesstyp der Oberfläche, wie ihn das Modell nennt — oder `null`, wo es ihn dort nicht gibt. */
export const projectTypeOf = (key: ProcessTypeKey): SitePerspectiveProjectType | null => {
    if (key === 'development') return SitePerspectiveProjectType.development;
    if (key === 'residentialPurchase') return SitePerspectiveProjectType.residentialPurchase;
    if (key === 'landDevelopment') return SitePerspectiveProjectType.landDevelopment;
    if (key === 'existingAssetAcquisition') return SitePerspectiveProjectType.existingAssetAcquisition;

    // Neubau, Ankauf und Verkauf haben ihren Verlauf bis heute in der Oberfläche und keine
    // Anforderungen im Modell. Das ist keine Lücke hier, sondern der Stand dort.
    return null;
};

/**
 * Alle Dokumente, die diese Phase verlangt — über ihre Perspektiven hinweg.
 *
 * Der Datenraum steht neben der ganzen Phase und nicht neben einer Perspektive; eine Anforderung
 * je Perspektive getrennt zu zeigen hieße, dieselbe Datei mehrfach zu vermissen. Doppelte fallen
 * deshalb heraus: Ein Grundbuchauszug, den zwei Perspektiven derselben Phase verlangen, ist ein
 * fehlendes Dokument, nicht zwei.
 */
export const requiredSlots = (
    typeKey: ProcessTypeKey,
    phase: string | null | undefined,
): DocumentSlot[] => {
    const projectType = projectTypeOf(typeKey);
    if (!projectType || !phase) return [];

    const process = processFor(projectType) ?? [];
    const entry = process.find((step) => step.phase === (phase as SitePerspectivePhase));
    if (!entry) return [];

    const seen = new Set<string>();

    return entry.perspectives
        .flatMap((requirement) =>
            documentSlotsFor(projectType, entry.phase, requirement.type),
        )
        .filter((slot) => {
            // Trennzeichen ist \u0000 und kein Leerzeichen: Rollen- und Untertypnamen enthalten
            // selbst welche, und «Grundbuchauszug» + «Abt II» träfe sonst
            // «Grundbuchauszug Abt» + «II».
            const key = `${slot.role}\u0000${slot.subtype ?? ''}`;
            if (seen.has(key)) return false;

            seen.add(key);
            return true;
        });
};

export interface AnalysedDocument {
    roleName?: string | null;
    subtypeName?: string | null;
}

/**
 * Welche der geforderten Dokumente im Datenraum noch fehlen.
 *
 * Ein Slot gilt als erfüllt, sobald eine ausgewertete Datei seine Rolle trägt — und, wo der Slot
 * einen Untertyp nennt, auch diesen.
 *
 * **Die Rolle allein genügt, wo der Slot keinen Untertyp nennt.** Sonst bliebe eine Anforderung
 * offen, die erfüllt ist: „irgendein Wartungsvertrag" ist erfüllt, sobald einer da ist, gleich
 * welcher Art.
 *
 * Umgekehrt erfüllt ein Dokument ohne erkannten Untertyp keinen Slot, der einen verlangt. Das ist
 * die härtere Lesart und die richtige: Eine Auswertung, die die Unterart nicht bestimmen konnte,
 * hat nicht festgestellt, dass es die verlangte ist.
 */
export const missingSlots = (
    required: DocumentSlot[],
    analysed: AnalysedDocument[],
): DocumentSlot[] => {
    const present = (analysed ?? [])
        .map((entry) => ({
            role: String(entry?.roleName ?? '').trim(),
            subtype: String(entry?.subtypeName ?? '').trim(),
        }))
        .filter((entry) => entry.role);

    return (required ?? []).filter(
        (slot) =>
            !present.some(
                (entry) =>
                    entry.role === slot.role && (!slot.subtype || entry.subtype === slot.subtype),
            ),
    );
};
